import { useCallback, useLayoutEffect, useMemo, type FormEvent } from 'react';
import isEqual from 'lodash/isEqual';
import { FormProvider, useFieldArray, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Paper,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';

import { usePortalContext } from '@/contexts/PortalProvider';

import { TripLegType } from '@/data/models/reservation.model';
import { ServicePlan } from '@/data/models/service-plan.model';
import { selectPassengerClientData } from '@/data/client.slice';
import { selectCurrentPassenger } from '@/data/passenger.slice';
import {
  emptyTripSegment,
  getDefaultPickupPhoneAndExtensionFromPassenger,
} from './schema/reservationForm.schema';

import { useAppSelector } from '@/hooks/useAppSelector';

import { useDebouncedReservationActionSelectors } from './hooks/useDebouncedReservationActionSelectors';
import { LevelOfCareContext } from './machines/levelOfCare.machine';
import { useFieldActorSnapshot } from './hooks/useFieldActorSnapshot';
import { mergeAggregatedWithLiveForm } from './utils/getAggregatedFormValues';
import {
  createTripLegReservationFormSchema,
  emptyReservationFormValues,
  type ReservationFormInput,
  type ReservationFormValues,
} from './schema/reservationForm.schema';
import { useReservationFormMachines } from './ReservationFormMachinesContext';
import { useReservationFieldEditValidation } from './hooks/useReservationFieldEditValidation';

import { MultiTripLegsSection } from './fields/MultiTripLegsSection';
import { TripLegFieldsRow } from './fields/TripLegFieldsRow';
import { TripMachineCountSync } from './fields/TripMachineCountSync';
import { TripTypeField } from './fields/TripTypeField';
import { StandingOrderForm } from './fields/StandingOrderForm';
import PassengerBenefits from '../home/passengers/PassengerBenefits';
import BookTripSupport from '../member/reservation/BookTripSupport';
import { MultiLegTitle } from '@/components/reservations/MultiLegTitle';
import { ReservationTripLegAside } from './ReservationTripLegAside';

const PREFIX = 'ReservationTripLegForm';

const classes = {
  container: `${PREFIX}-container`,
  title: `${PREFIX}-title`,
  form: `${PREFIX}-form`,
  isEditing: `${PREFIX}-isEditing`,
  isAdding: `${PREFIX}-isAdding`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    marginBottom: theme.spacing(2),
    paddingTop: 0,
    paddingBottom: theme.spacing(2),
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 21,
    marginBottom: theme.spacing(1),
  },
  [`& .${classes.form}`]: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    gap: theme.spacing(2),
    flex: 1,
    width: '-webkit-fill-available',
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(4),
    },
    [`&.${classes.isEditing}`]: {
      padding: theme.spacing(0),
    },
    [`&.${classes.isAdding}`]: {
      padding: theme.spacing(0),
    },
  },
}));

const FormContainer = ({
  isEditing = false,
  isAdding = false,
  children,
}: {
  isEditing?: boolean;
  isAdding?: boolean;
  children: React.ReactNode;
}) => {
  const theme = useTheme();
  const elevation = useMemo(
    () => (isEditing || isAdding ? 0 : 2),
    [isEditing, isAdding],
  );
  const classNames = useMemo(() => {
    return `${classes.form} ${isEditing ? classes.isEditing : ''} ${isAdding ? classes.isAdding : ''}`;
  }, [isEditing, isAdding]);

  return (
    <Paper elevation={elevation} className={classNames}>
      {children}
    </Paper>
  );
};

interface ReservationTripLegFormProps {
  form: UseFormReturn<ReservationFormInput, unknown, ReservationFormValues>;
  isEditing?: boolean;
  isAdding?: boolean;
  onNext?: (evt: FormEvent) => void;
}

function ReservationTripLegForm({
  form,
  isEditing = false,
  isAdding = false,
  onNext,
}: ReservationTripLegFormProps) {
  const { isPortal, isMemberPortal } = usePortalContext();
  const { t } = useTranslation('common');
  const tripLegFormSchema = useMemo(
    () => createTripLegReservationFormSchema(null, t),
    [t],
  );
  const theme = useTheme();

  const passengerClientData = useAppSelector(selectPassengerClientData);
  const passenger = useAppSelector(selectCurrentPassenger);

  const { mainActor, mainSnapshot, fieldRefs } = useReservationFormMachines();
  const levelOfCareSnap = useFieldActorSnapshot(fieldRefs.levelOfCare);

  const { append, remove, fields } = useFieldArray({
    control: form.control,
    name: 'trips',
  });

  const tripType = form.watch('tripType');
  const watchedTrips = form.watch('trips');
  const { isCareLevelMismatch } = levelOfCareSnap.context as LevelOfCareContext;
  const tripLegCount = watchedTrips?.length ?? 0;
  const hasCareLevelMismatch = isCareLevelMismatch
    .slice(0, tripLegCount)
    .some(Boolean);
  /** Re-merge when override / member fields change (machines only hold trips). */
  const topLevelFormWatch = form.watch([
    'AllowOverride',
    'OverrideFirstName',
    'OverrideLastName',
    'OverrideReason',
    'OverrideServicePlan',
    'MemberHomePhone',
    'MemberMobilePhone',
    'genericErrors',
  ]);

  const legIndex = watchedTrips?.length ?? 0;

  const showStandingOrderForm =
    !isPortal && tripType !== TripLegType.MultiLeg && !isEditing && !isAdding;

  const handleSaveAsDraft = () => {
    const snap = mainActor.getSnapshot();
    const mergedValues = mergeAggregatedWithLiveForm(
      snap.context,
      form.getValues(),
    );
    mainActor.send({ type: 'SAVE_AS_DRAFT', formValues: mergedValues });
  };

  const handleOverrideOpen = () => {
    mainActor.send({ type: 'OVERRIDE_OPEN' });
  };

  const isSubmitting =
    mainSnapshot.matches('submitting') || mainSnapshot.matches('validating');

  useReservationFieldEditValidation(form);

  const mergedValues = useMemo(() => {
    return mergeAggregatedWithLiveForm(mainSnapshot.context, form.getValues());
  }, [mainSnapshot.context, tripType, watchedTrips, topLevelFormWatch]);

  /**
   * Sending merged values to the main machine so footer selectors stay in sync with the form.
   */
  useLayoutEffect(() => {
    const existing = mainActor.getSnapshot().context.tripLegMergedValues;
    if (existing !== undefined && isEqual(existing, mergedValues)) {
      return;
    }
    mainActor.send({
      type: 'TRIP_LEG_MERGED_VALUES',
      mergedValues,
    });
  }, [mainActor, mergedValues]);

  const {
    tripLegNextButtonDisabled,
    requestOverrideButtonDisabled,
    saveAsDraftButtonDisabled,
  } = useDebouncedReservationActionSelectors(mainSnapshot, {
    isMemberPortal,
    hasCareLevelMismatch,
  });

  const handleNextStep = (evt: FormEvent) => {
    if (isSubmitting) return;
    if (!tripLegFormSchema.safeParse(mergedValues).success) return;
    onNext?.(evt);
  };

  const handleReset = useCallback(() => {
    form.reset(emptyReservationFormValues);
    mainActor.send({ type: 'RESET_ALL' });
  }, [form, mainActor]);

  const handleAddTripLeg = () => {
    append(
      {
        ...emptyTripSegment,
        tripLegType: form.getValues('tripType'),
        ...getDefaultPickupPhoneAndExtensionFromPassenger(passenger),
      },
      { shouldFocus: false },
    );
  };

  return (
    <FormProvider {...form}>
      <LocalizationProvider dateAdapter={AdapterLuxon}>
        <TripMachineCountSync />
        <StyledBox
          component="form"
          className={classes.container}
          onSubmit={(evt) => {
            evt.preventDefault();
            handleNextStep(evt);
          }}>
          <FormContainer isEditing={isEditing} isAdding={isAdding}>
            {!isEditing && !isAdding && (
              <Typography className={classes.title} variant="h2">
                {t('member.tripDetails')}
              </Typography>
            )}
            {isEditing && tripType !== TripLegType.MultiLeg && (
              <MultiLegTitle legIndexReadable={legIndex} />
            )}
            {!isEditing && (
              <Box sx={isAdding ? { display: 'none' } : undefined}>
                <TripTypeField />
              </Box>
            )}
            {tripType === TripLegType.MultiLeg ? (
              <MultiTripLegsSection fields={fields} remove={remove} />
            ) : (
              <TripLegFieldsRow
                index={0}
                showRoundTripReturnFields={tripType === TripLegType.RoundTrip}
              />
            )}
            {showStandingOrderForm && <StandingOrderForm index={0} />}
            {!isEditing && !isAdding && (
              <Box
                sx={{
                  display: 'flex',
                  gap: theme.spacing(2),
                  flexWrap: 'wrap',
                  marginTop: theme.spacing(2),
                  marginBottom: theme.spacing(2),
                }}>
                {tripType === TripLegType.MultiLeg ? (
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={handleAddTripLeg}>
                    {t('reservation.addLeg')}
                  </Button>
                ) : null}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={tripLegNextButtonDisabled}
                  data-fs-element="CreateReservationStep1NextButton">
                  {t('reservation.next')}
                </Button>
                {!isMemberPortal && (
                  <>
                    <Button
                      color="secondary"
                      variant="contained"
                      disabled={saveAsDraftButtonDisabled}
                      onClick={handleSaveAsDraft}
                      data-fs-element="CreateReservationSaveAsDraftButton">
                      {t('reservation.saveAsDraft')}
                    </Button>
                    <Button
                      color="secondary"
                      variant="contained"
                      disabled={requestOverrideButtonDisabled}
                      onClick={handleOverrideOpen}
                      data-fs-element="CreateReservationRequestOverrideStartButton">
                      {t('reservation.requestOverride')}
                    </Button>
                  </>
                )}
              </Box>
            )}
          </FormContainer>
          {!isEditing && !isAdding && (
            <ReservationTripLegAside
              form={form}
              passengerClientData={passengerClientData}
            />
          )}
        </StyledBox>
      </LocalizationProvider>
    </FormProvider>
  );
}

export { type ReservationTripLegFormProps, ReservationTripLegForm };
